import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Cancel as RevokeIcon,
  AccessTime as TimeIcon,
  CheckCircle as ActiveIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../animations/variants';
import { getActiveSessions, revokeSession } from '../../services/jitApi';

const JITActiveSessions = () => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery('(max-width:767px)');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchActiveSessions();
  }, []);

  const fetchActiveSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getActiveSessions();
      setSessions(response.data || []);
    } catch (err) {
      console.error('Failed to fetch active sessions:', err);
      setError('Failed to load active sessions');
    } finally {
      setLoading(false);
    }
  };

  const openRevokeDialog = (session) => {
    setSelectedSession(session);
    setRevokeReason('');
    setRevokeDialogOpen(true);
  };

  const handleRevoke = async () => {
    if (!selectedSession) return;

    setProcessing(true);
    try {
      await revokeSession(selectedSession.id, revokeReason);
      toast.success('Session revoked successfully');
      setRevokeDialogOpen(false);
      fetchActiveSessions();
    } catch (err) {
      console.error('Failed to revoke session:', err);
      toast.error('Failed to revoke session');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;

    if (diff <= 0) return { text: 'Expired', percent: 0, color: '#E74C3C' };

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    // Calculate percentage based on typical 8h max duration
    const maxDuration = 8 * 60 * 60 * 1000;
    const percent = Math.min(100, (diff / maxDuration) * 100);

    let color = '#27AE60';
    if (percent < 25) color = '#E74C3C';
    else if (percent < 50) color = '#F39C12';

    const text = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

    return { text, percent, color };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#4698D3' }} />
      </Box>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Header */}
      <motion.div variants={sectionVariants}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: { xs: 3, sm: 4 } }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Bitter, Georgia, serif',
              fontWeight: 600,
              color: '#253E66',
              mb: 1,
            }}
          >
            Active Sessions
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
            }}
          >
            View and manage your currently active JIT access sessions
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            onClick={fetchActiveSessions}
            sx={{
              border: '1px solid #E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#4698D3',
                backgroundColor: 'rgba(70, 152, 211, 0.04)',
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <motion.div variants={sectionVariants}>
      {sessions.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'rgba(70, 152, 211, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 2,
            }}
          >
            <ActiveIcon sx={{ fontSize: 32, color: '#4698D3' }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              color: '#253E66',
              mb: 1,
            }}
          >
            No Active Sessions
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
            }}
          >
            You don't have any active JIT access sessions
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {sessions.map((session, index) => {
            const timeRemaining = getTimeRemaining(session.expiresAt);
            return (
              <Paper
                key={session.id}
                elevation={0}
                sx={{
                  '@keyframes fadeInItem': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                  p: 2,
                  border: '1px solid #E5E5E5',
                  borderRadius: '8px',
                }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    color: '#253E66',
                    mb: 1.5,
                  }}
                >
                  {session.accountName || session.accountId}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', flexShrink: 0 }}
                  >
                    Permission Set
                  </Typography>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                      color: '#333333',
                      ml: 2,
                      textAlign: 'right',
                    }}
                  >
                    {session.permissionSetName || session.permissionSetId}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', flexShrink: 0 }}
                  >
                    Granted At
                  </Typography>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      color: '#333333',
                      ml: 2,
                      textAlign: 'right',
                    }}
                  >
                    {formatDate(session.grantedAt)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pt: 1.5,
                    borderTop: '1px solid #F0F0F0',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label="Active"
                      icon={<ActiveIcon sx={{ fontSize: 14 }} />}
                      color="success"
                      sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}
                    />
                    <Chip
                      size="small"
                      icon={<TimeIcon sx={{ fontSize: 14 }} />}
                      label={timeRemaining.text}
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 500,
                        color: timeRemaining.color,
                        borderColor: timeRemaining.color,
                      }}
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Revoke Access">
                      <IconButton
                        size="small"
                        onClick={() => openRevokeDialog(session)}
                        sx={{
                          color: '#E74C3C',
                          '&:hover': {
                            backgroundColor: 'rgba(231, 76, 60, 0.08)',
                          },
                        }}
                      >
                        <RevokeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            overflowX: 'auto',
          }}
        >
          <Table sx={{ minWidth: { xs: 450, sm: 700 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Account
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Permission Set
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Granted At
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Time Remaining
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Status
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session, index) => {
                const timeRemaining = getTimeRemaining(session.expiresAt);
                return (
                  <TableRow
                    key={session.id}
                    hover
                    sx={{
                      '@keyframes fadeInRow': {
                        from: { opacity: 0, transform: 'translateY(8px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                      animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`,
                      '&:hover': {
                        backgroundColor: 'rgba(70, 152, 211, 0.02)',
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          color: '#253E66',
                        }}
                      >
                        {session.accountName || session.accountId}
                      </Typography>
                      {session.accountName && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#666666',
                            fontFamily: 'Metropolis, sans-serif',
                          }}
                        >
                          {session.accountId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          color: '#333333',
                        }}
                      >
                        {session.permissionSetName || session.permissionSetId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          color: '#666666',
                        }}
                      >
                        {formatDate(session.grantedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 120 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <TimeIcon sx={{ fontSize: 16, color: timeRemaining.color }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 600,
                              color: timeRemaining.color,
                            }}
                          >
                            {timeRemaining.text}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={timeRemaining.percent}
                          sx={{
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: timeRemaining.color,
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label="Active"
                        icon={<ActiveIcon sx={{ fontSize: 14 }} />}
                        color="success"
                        sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Revoke Access">
                        <IconButton
                          size="small"
                          onClick={() => openRevokeDialog(session)}
                          sx={{
                            color: '#E74C3C',
                            '&:hover': {
                              backgroundColor: 'rgba(231, 76, 60, 0.08)',
                            },
                          }}
                        >
                          <RevokeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </motion.div>

      {/* Revoke Confirmation Dialog */}
      <Dialog
        open={revokeDialogOpen}
        onClose={() => !processing && setRevokeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#E74C3C' }}>
          Revoke Access
        </DialogTitle>
        <DialogContent>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This action will immediately revoke your access to this AWS account. You will need to submit a new request to regain access.
          </Alert>
          {selectedSession && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: '#F9FBFD',
                border: '1px solid #E5E5E5',
                borderRadius: '6px',
              }}
            >
              <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                Session Details
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <Chip size="small" label={selectedSession.accountId} />
                <Chip size="small" label={selectedSession.permissionSetName || selectedSession.permissionSetId} />
              </Box>
            </Paper>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (optional)"
            placeholder="Provide a reason for revoking this access..."
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'Metropolis, sans-serif',
              },
            }}
          />
          </motion.div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRevokeDialogOpen(false)}
            disabled={processing}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRevoke}
            disabled={processing}
            sx={{
              backgroundColor: '#E74C3C',
              fontFamily: 'Metropolis, sans-serif',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#C0392B',
              },
            }}
          >
            {processing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Revoke Access'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default JITActiveSessions;
